import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * A reading-level pick captured while guest-browsing. We keep both halves
 * (mirrors the iOS `GuestPreferencesStore`):
 *   - `alias` feeds the `required_reading_level` book filter (OR-matched).
 *   - `id` pre-seeds the reading-level step of onboarding once the visitor
 *     creates an account.
 */
export interface GuestReadingLevel {
  id: string;
  alias: string;
}

/**
 * GuestStore — the only state a not-signed-in visitor needs. "Guest" itself
 * is derived from Auth0 (`useIsGuest` = `!isAuthenticated`), so this store
 * holds just the reading-level selection, persisted to localStorage so it
 * survives reloads and the Auth0 redirect during sign-up.
 *
 * `hasChosenLevels` is tracked separately from a non-empty `readingLevels`
 * so "Browse all levels" (an empty pick = no filter) still counts as a
 * choice and dismisses the intro overlay.
 */
export interface GuestState {
  readingLevels: GuestReadingLevel[];
  hasChosenLevels: boolean;
  /** Record the visitor's pick. An empty array means "browse all levels". */
  chooseReadingLevels: (levels: GuestReadingLevel[]) => void;
  /** Wipe the guest selection — called once onboarding has consumed it. */
  clear: () => void;
}

export const useGuestStore = create<GuestState>()(
  persist(
    (set) => ({
      readingLevels: [],
      hasChosenLevels: false,
      chooseReadingLevels: (readingLevels) => set({ readingLevels, hasChosenLevels: true }),
      clear: () => set({ readingLevels: [], hasChosenLevels: false }),
    }),
    {
      name: 'guest-preferences',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
